
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { MoreVertical, Edit, Trash2, Target, Copy } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/lib/toast";
import { EditColumnDialog } from "./EditColumnDialog";
import { SetGoalColumnDialog } from "./SetGoalColumnDialog";
import { DuplicateColumnDialog } from "./DuplicateColumnDialog";

interface ColumnOptionsDropdownProps {
  columnId: string;
  columnName: string;
  boardId: string;
  isGoalColumn: boolean;
  goalId?: string;
  onRefresh: () => void;
}

export function ColumnOptionsDropdown({
  columnId,
  columnName,
  boardId,
  isGoalColumn,
  goalId,
  onRefresh,
}: ColumnOptionsDropdownProps) {
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [goalDialogOpen, setGoalDialogOpen] = useState(false);
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const queryClient = useQueryClient();

  const deleteColumnMutation = useMutation({
    mutationFn: async () => {
      // First, move all cards to archived
      await supabase
        .from("kanban_cards")
        .update({ is_archived: true })
        .eq("column_id", columnId);

      // Then delete the column
      const { error } = await supabase
        .from("kanban_columns")
        .delete()
        .eq("id", columnId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["kanban-columns", boardId] });
      queryClient.invalidateQueries({ queryKey: ["kanban-cards", boardId] });
      toast.success("Coluna excluída com sucesso!");
      onRefresh();
    },
    onError: (error) => {
      console.error("Error deleting column:", error);
      toast.error("Erro ao excluir coluna");
    },
  });

  const removeGoalMutation = useMutation({
    mutationFn: async () => {
      const { error } = await supabase
        .from("kanban_columns")
        .update({
          is_goal_column: false,
          goal_id: null,
        })
        .eq("id", columnId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["kanban-columns", boardId] });
      toast.success("Meta removida da coluna!");
      onRefresh();
    },
    onError: (error) => {
      console.error("Error removing goal:", error);
      toast.error("Erro ao remover meta");
    },
  });

  return (
    <>
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
            <MoreVertical className="h-4 w-4" />
            <span className="sr-only">Opções da coluna</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent 
          align="end" 
          className="w-48 bg-popover border shadow-md z-50 fixed"
          side="bottom"
          sideOffset={4}
          avoidCollisions={true}
          collisionPadding={8}
        >
          <DropdownMenuItem onClick={() => setEditDialogOpen(true)}>
            <Edit className="mr-2 h-4 w-4" />
            Renomear coluna
          </DropdownMenuItem>
          
          <DropdownMenuItem onClick={() => setDuplicateDialogOpen(true)}>
            <Copy className="mr-2 h-4 w-4" />
            Duplicar coluna
          </DropdownMenuItem>

          <DropdownMenuSeparator />

          {isGoalColumn ? (
            <DropdownMenuItem onClick={() => removeGoalMutation.mutate()}>
              <Target className="mr-2 h-4 w-4" />
              Remover meta
            </DropdownMenuItem>
          ) : (
            <DropdownMenuItem onClick={() => setGoalDialogOpen(true)}>
              <Target className="mr-2 h-4 w-4" />
              Definir como meta
            </DropdownMenuItem>
          )}

          <DropdownMenuSeparator />

          <DropdownMenuItem
            onClick={() => deleteColumnMutation.mutate()}
            className="text-destructive"
          >
            <Trash2 className="mr-2 h-4 w-4" />
            Excluir coluna
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <EditColumnDialog
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        columnId={columnId}
        currentName={columnName}
        onRefresh={onRefresh}
      />

      <SetGoalColumnDialog
        isOpen={goalDialogOpen}
        onClose={() => setGoalDialogOpen(false)}
        columnId={columnId}
        columnName={columnName}
        boardId={boardId}
        currentGoalId={goalId}
        onSuccess={onRefresh}
      />

      <DuplicateColumnDialog
        open={duplicateDialogOpen}
        onOpenChange={setDuplicateDialogOpen}
        columnId={columnId}
        columnName={columnName}
        boardId={boardId}
      />
    </>
  );
}
